import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { Student } from '../interfaces/student.interfaces';
import {
  BackendStudent,
  mapBackendStudentToFrontend,
  mapFrontendStudentToBackend,
} from '../mappers/student.mapper';
import { environment } from '../../environments/environment';

interface ApiListResponse {
  status: string;
  results: number;
  data: { documents: BackendStudent[] };
}

interface ApiDocumentResponse {
  status: string;
  data: { document: BackendStudent };
}

@Injectable({
  providedIn: 'root',
})
export class StudentService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.apiUrl}/student`;

  getAllStudents(): Observable<Student[]> {
    return this.http
      .get<ApiListResponse>(`${this.baseUrl}/`)
      .pipe(
        map((res) =>
          res.data.documents.map(mapBackendStudentToFrontend),
        ),
      );
  }

  getStudentById(id: string): Observable<Student> {
    return this.http
      .get<ApiDocumentResponse>(`${this.baseUrl}/${id}`)
      .pipe(map((res) => mapBackendStudentToFrontend(res.data.document)));
  }

  getStudentByAuthId(authId: string): Observable<Student> {
    return this.http
      .get<ApiDocumentResponse>(`${this.baseUrl}/auth/${authId}`)
      .pipe(map((res) => mapBackendStudentToFrontend(res.data.document)));
  }

  getMyProfile(): Observable<Student> {
    return this.http
      .get<ApiDocumentResponse>(`${this.baseUrl}/auth/${localStorage.getItem('userId') || ''}`)
      .pipe(map((res) => mapBackendStudentToFrontend(res.data.document)));
  }

  updateMyProfile(data: {
    telefono?: string;
    correoPersonal?: string;
    bioPersonal?: string;
    facebookUsuario?: string;
    instagramUsuario?: string;
    tiktokUsuario?: string;
    xUsuario?: string;
    youtubeUsuario?: string;
    linkedinUsuario?: string;
  }): Observable<Student> {
    return this.http
      .patch<ApiDocumentResponse>(`${this.baseUrl}/me`, data)
      .pipe(map((res) => mapBackendStudentToFrontend(res.data.document)));
  }

  updateStudent(
    id: string,
    student: Partial<Student> | any,
  ): Observable<Student> {
    const backendPayload = student.fullName || student.matricula
      ? mapFrontendStudentToBackend(student as Student)
      : student;

    return this.http
      .patch<ApiDocumentResponse>(
        `${this.baseUrl}/${id}`,
        backendPayload,
      )
      .pipe(map((res) => mapBackendStudentToFrontend(res.data.document)));
  }

  updateStudentAccountStatus(
    id: string,
    accountStatus: 'pending' | 'active' | 'inactive' | 'rejected',
    reason?: string,
    comments?: string,
  ): Observable<Student> {
    return this.http
      .patch<ApiDocumentResponse>(
        `${this.baseUrl}/${id}/account-status`,
        { accountStatus, reason, comments },
      )
      .pipe(
        map((res) =>
          mapBackendStudentToFrontend(
            res.data.document,
          ),
        ),
      );
  }

  updateStudentDocuments(
    id: string,
    documents: {
      cartaAceptacion?: string;
      cartaTermino?: string;
      constanciaPlatica?: string;
      reporteActividades?: string;
      reporteFinal?: string;
      cloudUrls?: {
        ine?: string;
        cv?: string;
        comprobante?: string;
      };
      [key: string]: any;
    },
  ): Observable<Student> {
    return this.http
      .patch<ApiDocumentResponse>(
        `${this.baseUrl}/${id}`,
        documents,
      )
      .pipe(map((res) => mapBackendStudentToFrontend(res.data.document)));
  }

  deleteStudent(id: string): Observable<unknown> {
    return this.http.delete(`${this.baseUrl}/${id}`);
  }

  updateHours(
    id: string,
    horasAcreditadas: number,
    horasCubrir: number,
  ): Observable<Student> {
    const horasPendientes = Math.max(0, horasCubrir - horasAcreditadas);

    return this.http
      .patch<ApiDocumentResponse>(
        `${this.baseUrl}/${id}`,
        {
          horasAcreditadas,
          horasCubrir,
          horasPendientes,
        },
      )
      .pipe(
        map((res) =>
          mapBackendStudentToFrontend(
            res.data.document,
          ),
        ),
      );
  }

  acceptStudent(id: string, comments?: string): Observable<Student> {
    return this.updateStudentAccountStatus(id, 'active', 'admin_approval', comments);
  }

  rejectStudent(id: string, comments?: string): Observable<Student> {
    return this.updateStudentAccountStatus(id, 'rejected', 'admin_rejection', comments);
  }

  /**
   * Fase D.3 (RA1-1): marca primerIngresoRealizado=true tras el primer ingreso
   * del alumno ya activo. Ruta backend real (coordinador D.3):
   *   PATCH {apiUrl}/student/:id/primer-ingreso
   */
  markPrimerIngreso(id: string): Observable<Student> {
    return this.http
      .patch<ApiDocumentResponse>(
        `${this.baseUrl}/${id}/primer-ingreso`,
        {},
      )
      .pipe(map((res) => mapBackendStudentToFrontend(res.data.document)));
  }
}
